import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput10 from "@/components/site/PhoneInput10";
import DoctorNameInput from "@/components/site/DoctorNameInput";
import { toE164, isValid10Digit } from "@/lib/phone";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkle, ArrowRight, Info } from "@phosphor-icons/react";

/* ===========================================================================
   AddDoctorDialog — iter100.272 · Slim admin create (2026-07-31)
   ---------------------------------------------------------------------------
   Per user direction: admins create a doctor as a LOGIN SHELL only — the
   doctor owns their profile (bio, specialization, degree, DCI, image, fees,
   hours). This dialog now collects exactly three things:

     1. Full name — needed to greet the doctor in the invite ("Hi Dr. X…")
     2. Phone     — login identity. Stored on `users.phone` only. Does NOT
                    populate the doctor's patient-facing "Phone" field —
                    the doctor fills that themselves on My Profile.
     3. Clinic    — optional. Ops disambiguation only ("Dr. Sharma of
                    Clinic A vs Clinic B"). Never patient-facing until
                    the doctor confirms it on their profile.

   Everything else previously collected (specialty, city, address, degree,
   DCI, state council, years, fee, image, bio) is now doctor-owned. The
   admin dropdowns for those have been removed from this dialog by design.

   Matches Practo / Zocdoc / Airbnb / Salesforce "invite + doctor owns
   profile" pattern.
   =========================================================================== */

const EMPTY = { name: "", phone: "", clinic_name_hint: "" };

export default function AddDoctorDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const reset = () => setTimeout(() => { setForm(EMPTY); setSaving(false); }, 200);

    const handleOpenChange = (next) => {
        onOpenChange?.(next);
        if (!next) reset();
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name.trim()) return toast.error("Provider name is required");
        if (!isValid10Digit(form.phone)) return toast.error("Enter the doctor's 10-digit mobile number");

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: toE164(form.phone),
                clinic_name_hint: form.clinic_name_hint.trim() || null,
            };
            const r = await api.post("/admin/doctors", payload);
            toast.success(`${r.data.name} created. They can log in and complete their own profile.`);
            handleOpenChange(false);
            onCreated?.(r.data.id);
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 409) toast.error(detail || "A user with this phone already exists");
            else toast.error(detail || "Failed to create provider");
        }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-lg" data-testid="add-doctor-dialog">
                <form onSubmit={submit}>
                    <DialogHeader>
                        <div className="overline text-primary mb-1.5 flex items-center gap-1.5">
                            <Sparkle size={11} weight="fill" /> Invite provider
                        </div>
                        <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                            Create a login for the provider
                        </DialogTitle>
                        <DialogDescription className="text-sm">
                            Just a login shell. On first sign-in, the provider fills their
                            own profile — bio, specialty, credentials, photo, fees, hours.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-5 space-y-4">
                        <div>
                            <Label>Provider name *</Label>
                            <div className="h-11 mt-1.5">
                                <DoctorNameInput
                                    value={form.name}
                                    onChange={(v) => upd("name", v)}
                                    inputClassName="h-11"
                                    testid="add-doc-name"
                                />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1.5">No need to type &quot;Dr.&quot; — we add it for you.</p>
                        </div>

                        <div>
                            <Label>Login phone *</Label>
                            <div className="h-11 mt-1.5">
                                <PhoneInput10
                                    value={form.phone}
                                    onChange={(v) => upd("phone", v)}
                                    inputClassName="h-11"
                                    testid="add-doc-phone"
                                />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1.5">Used only for OTP login. Never shown on their patient-facing profile.</p>
                        </div>

                        <div>
                            <Label>Clinic name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Input
                                value={form.clinic_name_hint}
                                onChange={(e) => upd("clinic_name_hint", e.target.value)}
                                placeholder="Helps you distinguish two providers with the same name"
                                className="h-11 mt-1.5"
                                data-testid="add-doc-clinic-hint"
                            />
                            <p className="text-xs text-muted-foreground mt-1.5">The provider sets their public clinic name themselves.</p>
                        </div>

                        <div className="rounded-xl border border-primary/20 bg-primary/5 px-3.5 py-2.5 flex items-start gap-2.5 text-xs" data-testid="add-doc-info-banner">
                            <Info size={16} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                            <div className="text-foreground/80 leading-snug">
                                After creating, the provider stays in <strong>draft</strong> until they log in
                                and complete their own profile. Their public listing goes live only after
                                that + admin verification.
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                        <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full" data-testid="add-doc-cancel-btn">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="rounded-full" data-testid="add-doc-submit-btn">
                            {saving ? "Creating…" : (<>Create login <ArrowRight size={14} className="ml-1.5" /></>)}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
